import { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Globe, Copy, RefreshCw, Ban, Trash2, Eye } from "lucide-react";
import { toast } from "sonner";

const portalUrl = (token) => `${window.location.origin}/portal/${token}`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");

/** Broker-side management of the seller / client portal link. */
export default function SellerPortalTab({ dealId }) {
    const [p, setP] = useState(null);
    const [freshUrl, setFreshUrl] = useState(null); // shown once: the raw token is never retrievable again
    const [welcome, setWelcome] = useState("");
    const [days, setDays] = useState("90");
    const [post, setPost] = useState({ title: "", body: "" });

    const load = useCallback(async () => {
        const { data } = await api.get(`/seller-portal/deal/${dealId}`);
        setP(data); setWelcome(data.welcome_message || "");
    }, [dealId]);
    useEffect(() => { load(); }, [load]);

    const fail = (e) => toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : e.message);
    const create = async () => {
        if (p?.exists && !confirm("Create a new link? The current link will stop working immediately.")) return;
        try {
            const { data } = await api.post(`/seller-portal/deal/${dealId}`, { expires_in_days: parseInt(days, 10) || 90, show_conditions: !!p?.show_conditions, welcome_message: welcome || null });
            setFreshUrl(portalUrl(data.token)); setP(data.portal);
        } catch (e) { fail(e); }
    };
    const patch = async (body, msg) => { try { const { data } = await api.patch(`/seller-portal/deal/${dealId}`, body); setP(data); msg && toast.success(msg); } catch (e) { fail(e); } };
    const revoke = async () => { if (!confirm("Revoke the link? The seller will lose access immediately.")) return; try { const { data } = await api.delete(`/seller-portal/deal/${dealId}`); setP(data); setFreshUrl(null); } catch (e) { fail(e); } };
    const addUpdate = async (e) => {
        e.preventDefault();
        if (!post.title.trim() || !post.body.trim()) return;
        try { await api.post(`/seller-portal/deal/${dealId}/updates`, post); setPost({ title: "", body: "" }); load(); } catch (e2) { fail(e2); }
    };
    const copy = async () => { await navigator.clipboard.writeText(freshUrl); toast.success("Link copied"); };

    if (!p) return <div className="text-sm text-muted-foreground p-4">Loading…</div>;
    const shared = new Set(p.shared_document_ids || []);
    const toggleDoc = (id) => { const n = new Set(shared); n.has(id) ? n.delete(id) : n.add(id); patch({ shared_document_ids: [...n] }); };

    return (
        <div className="space-y-4" data-testid="seller-portal-tab">
            <div className="border border-border rounded-md bg-white p-6 space-y-4">
                <div>
                    <h3 className="font-display font-bold tracking-tight flex items-center gap-2"><Globe className="w-4 h-4 text-gold" /> Seller portal</h3>
                    <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
                        A read-only page where the seller (your client) can follow progress, read your updates and download documents you choose to share. No login is needed, so the link is the key — share it only with them.
                        Fees, buyers, bids, notes and valuations are never shown.
                    </p>
                </div>

                {p.exists && (
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm" data-testid="portal-status">
                        <span className={p.active ? "text-emerald-700 font-medium" : "text-red-700 font-medium"}>{p.active ? "Link active" : p.revoked ? "Link revoked" : "Link expired"}</span>
                        <span className="text-muted-foreground text-xs">Expires {fmt(p.expires_at)}</span>
                        <span className="text-muted-foreground text-xs flex items-center gap-1"><Eye className="w-3 h-3" /> {p.access_count} view{p.access_count === 1 ? "" : "s"}{p.last_accessed_at ? ` · last ${fmt(p.last_accessed_at)}` : ""}</span>
                    </div>
                )}

                {freshUrl && (
                    <div className="border border-gold/60 bg-amber-50/50 rounded-md p-3" data-testid="portal-fresh-link">
                        <div className="text-xs text-muted-foreground mb-1">Copy this link now — it can't be shown again (you can create a new one at any time).</div>
                        <div className="flex gap-2"><Input readOnly value={freshUrl} className="font-mono text-xs" onFocus={(e) => e.target.select()} /><Button variant="outline" onClick={copy}><Copy className="w-4 h-4 mr-1.5" /> Copy</Button></div>
                    </div>
                )}

                <div className="grid md:grid-cols-4 gap-3 items-end">
                    <div className="md:col-span-3 space-y-1.5">
                        <label className="text-xs uppercase tracking-wider text-muted-foreground">Welcome message (optional)</label>
                        <Textarea rows={2} value={welcome} onChange={(e) => setWelcome(e.target.value)} onBlur={() => p.exists && welcome !== (p.welcome_message || "") && patch({ welcome_message: welcome }, "Saved")} data-testid="portal-welcome" />
                    </div>
                    <div className="flex gap-2 items-end">
                        {!p.exists && <div className="space-y-1.5 w-24"><label className="text-xs uppercase tracking-wider text-muted-foreground">Valid days</label><Input type="number" min="1" max="365" value={days} onChange={(e) => setDays(e.target.value)} /></div>}
                        <Button onClick={create} className="bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="portal-create-btn">
                            {p.exists ? <><RefreshCw className="w-4 h-4 mr-1.5" /> New link</> : "Create link"}
                        </Button>
                    </div>
                </div>

                {p.exists && p.active && (
                    <div className="flex flex-wrap items-center gap-4">
                        <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={!!p.show_conditions} onCheckedChange={(v) => patch({ show_conditions: !!v })} data-testid="portal-show-cps" /> Show conditions-precedent progress</label>
                        <Button variant="outline" size="sm" onClick={() => patch({ extend_days: 90 }, "Extended by 90 days")}>Extend 90 days</Button>
                        <Button variant="outline" size="sm" onClick={revoke} data-testid="portal-revoke-btn"><Ban className="w-3.5 h-3.5 mr-1.5" /> Revoke</Button>
                    </div>
                )}
            </div>

            {p.exists && (
                <>
                    <div className="border border-border rounded-md bg-white p-6">
                        <h3 className="font-display font-bold tracking-tight mb-1">Documents shared with the seller</h3>
                        <p className="text-xs text-muted-foreground mb-3">Only ticked documents can be seen or downloaded. Untick to withdraw access.</p>
                        {p.available_documents.length === 0 ? <div className="text-sm text-muted-foreground">No documents in the data room yet.</div> : (
                            <div className="grid md:grid-cols-2 gap-1" data-testid="portal-docs">
                                {p.available_documents.map((d) => (
                                    <label key={d.id} className="flex items-center gap-2 text-sm py-1 cursor-pointer"><Checkbox checked={shared.has(d.id)} onCheckedChange={() => toggleDoc(d.id)} /> <span className="truncate">{d.filename}</span><span className="text-xs text-muted-foreground">{d.category}</span></label>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="border border-border rounded-md bg-white p-6 space-y-3">
                        <h3 className="font-display font-bold tracking-tight">Updates for the seller</h3>
                        <form onSubmit={addUpdate} className="space-y-2" data-testid="portal-update-form">
                            <Input placeholder="Headline — e.g. Second management meeting held" maxLength={120} value={post.title} onChange={(e) => setPost((x) => ({ ...x, title: e.target.value }))} />
                            <Textarea rows={2} placeholder="What the seller should know (plain English, no buyer names)" maxLength={2000} value={post.body} onChange={(e) => setPost((x) => ({ ...x, body: e.target.value }))} />
                            <Button type="submit" size="sm" className="bg-foreground text-background hover:bg-neutral-800 hover:text-white">Post update</Button>
                        </form>
                        {p.updates.map((u) => (
                            <div key={u.id} className="border-t border-border pt-3 flex justify-between gap-3" data-testid={`portal-update-${u.id}`}>
                                <div><div className="text-sm font-medium">{u.title}</div><div className="text-sm text-muted-foreground whitespace-pre-line">{u.body}</div><div className="text-xs text-muted-foreground mt-1">{fmt(u.posted_at)}</div></div>
                                <Button size="icon" variant="ghost" onClick={async () => { await api.delete(`/seller-portal/updates/${u.id}`); load(); }}><Trash2 className="w-3.5 h-3.5 text-muted-foreground" /></Button>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
